import { Box, Button, Stack } from "@mui/material";
import {
  type ColumnDef,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { parseAsStringEnum, useQueryState } from "nuqs";
import { useCallback, useMemo } from "react";
import { toast } from "sonner";

import { useTableSearchParams } from "Forms/Hooks/useTableSearchParams";
import { useAppNavigate } from "Infrastructure/Routing/useAppNavigate";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { AppRouting } from "Infrastructure/Utils/UrlUtils";
import { TableSearchInput } from "Projects/Components/TableSearchInput";
import { useResolveRegistrationRequestMutation } from "RegistrationRequests/Api/Mutations/useResolveRegistrationRequestMutation";
import { useRegistrationRequestsQuery } from "RegistrationRequests/Api/Queries/useRegistrationRequestsQuery";
import {
  type RegistrationRequestListItemDto,
  RegistrationRequestListOrderBy,
  RegistrationRequestStatus,
  RegistrationRequestType,
  SortOrder,
} from "RegistrationRequests/Api/registrationRequestApi";
import {
  REGISTRATION_REQUEST_SORTABLE_COLUMNS,
  useRegistrationRequestsTableColumns,
} from "RegistrationRequests/Components/useRegistrationRequestsTableColumns";
import { BlTanstackTable } from "Shared/DataTable/BlTanstackTable";
import { BlDropdown } from "Shared/Inputs/BlDropdown";
import type { BlOption } from "Shared/Inputs/BlOption";

const SORT_COLUMN_MAP: Record<string, RegistrationRequestListOrderBy> = {
  name: RegistrationRequestListOrderBy.Name,
  type: RegistrationRequestListOrderBy.Type,
  status: RegistrationRequestListOrderBy.Status,
  dateCreated: RegistrationRequestListOrderBy.DateCreated,
};

export const RegistrationRequestsTable = (): React.ReactNode => {
  const { t } = useResource();
  const navigate = useAppNavigate();
  const resolveMutation = useResolveRegistrationRequestMutation();

  const [selectedStatus, setSelectedStatus] = useQueryState(
    "status",
    parseAsStringEnum<RegistrationRequestStatus>(
      Object.values(RegistrationRequestStatus),
    ).withDefault(RegistrationRequestStatus.Pending),
  );

  const [selectedType, setSelectedType] = useQueryState(
    "type",
    parseAsStringEnum<RegistrationRequestType>(
      Object.values(RegistrationRequestType),
    ),
  );

  const {
    search,
    debouncedSearch,
    onSearchChange,
    pageIndex,
    pageSize,
    onPaginationChange,
    resetPage,
    sorting,
    onSortingChange,
  } = useTableSearchParams({
    validSortIds: [...REGISTRATION_REQUEST_SORTABLE_COLUMNS],
  });

  const orderBy = sorting[0]?.id ? SORT_COLUMN_MAP[sorting[0].id] : undefined;
  const sortOrder = sorting[0]?.desc
    ? SortOrder.Descending
    : SortOrder.Ascending;

  const { data, isLoading } = useRegistrationRequestsQuery({
    offset: pageIndex * pageSize,
    limit: pageSize,
    searchQuery: debouncedSearch || undefined,
    status: selectedStatus,
    type: selectedType,
    orderBy,
    sortOrder,
  });

  const items = data?.items ?? [];
  const totalCount = data?.totalCount ?? 0;

  const statusOptions = useMemo(
    (): BlOption[] => [
      { value: "", label: t(Resources.RegistrationRequests.AllStatuses) },
      {
        value: RegistrationRequestStatus.Pending,
        label: t(Resources.RegistrationRequests.StatusPending),
      },
      {
        value: RegistrationRequestStatus.Resolved,
        label: t(Resources.RegistrationRequests.StatusResolved),
      },
    ],
    [t],
  );

  const typeOptions = useMemo(
    (): BlOption[] => [
      { value: "", label: t(Resources.RegistrationRequests.AllTypes) },
      {
        value: RegistrationRequestType.Partner,
        label: t(Resources.RegistrationRequests.TypePartner),
      },
      {
        value: RegistrationRequestType.Actor,
        label: t(Resources.RegistrationRequests.TypeActor),
      },
    ],
    [t],
  );

  const handleResolve = useCallback(
    async (registrationRequestID: string) => {
      try {
        await resolveMutation.mutateAsync(registrationRequestID);
        toast.success(t(Resources.RegistrationRequests.ResolveSuccess));
      } catch {
        // auto-toast shown by hook
      }
    },
    [resolveMutation, t],
  );

  const handleCreatePartner = useCallback(
    (row: { company: string | null; email: string; phone: string }) => {
      navigate(AppRouting.Partners.path(), {
        state: {
          prefillPartner: {
            companyName: row.company ?? "",
            email: row.email,
            phone: row.phone,
          },
        },
      });
    },
    [navigate],
  );

  const baseColumns = useRegistrationRequestsTableColumns();

  const columns = useMemo(() => {
    const actionIndex = baseColumns.findIndex((col) => col.id === "action");
    const actionsColumn = {
      id: "actions" as const,
      header: "",
      enableSorting: false,
      meta: { minWidth: 220 },
      cell: (info: {
        row: {
          original: {
            registrationRequestID: string;
            status: RegistrationRequestStatus;
            type: RegistrationRequestType;
            company: string | null;
            email: string;
            phone: string;
          };
        };
      }) => {
        const { original } = info.row;
        if (original.status !== RegistrationRequestStatus.Pending) {
          return null;
        }
        return (
          <Stack direction="row" spacing={Spacing.XS}>
            {original.type === RegistrationRequestType.Partner && (
              <Button
                size="small"
                variant="contained"
                onClick={(e) => {
                  e.stopPropagation();
                  handleCreatePartner(original);
                }}
              >
                {t(Resources.RegistrationRequests.CreatePartner)}
              </Button>
            )}
            <Button
              size="small"
              variant="outlined"
              onClick={(e) => {
                e.stopPropagation();
                handleResolve(original.registrationRequestID);
              }}
              disabled={resolveMutation.isPending}
            >
              {t(Resources.RegistrationRequests.Resolve)}
            </Button>
          </Stack>
        );
      },
    };

    const result = [...baseColumns];
    result.splice(
      actionIndex,
      0,
      actionsColumn as ColumnDef<RegistrationRequestListItemDto>,
    );
    return result;
  }, [
    baseColumns,
    t,
    resolveMutation.isPending,
    handleResolve,
    handleCreatePartner,
  ]);

  const table = useReactTable({
    data: items,
    columns,
    getRowId: (row) => row.registrationRequestID,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    manualSorting: true,
    rowCount: totalCount,
    state: { pagination: { pageIndex, pageSize }, sorting },
    onSortingChange,
    onPaginationChange,
  });

  return (
    <Box>
      <Stack
        direction="row"
        alignItems="center"
        spacing={Spacing.M}
        sx={{ marginBottom: Spacing.M }}
      >
        <Box sx={{ flexGrow: 1 }}>
          <TableSearchInput
            name="registration-request-search"
            value={search}
            onChange={onSearchChange}
            placeholder={t(Resources.RegistrationRequests.SearchPlaceholder)}
          />
        </Box>
        <Box sx={{ minWidth: 200 }}>
          <BlDropdown
            name="status-filter"
            options={statusOptions}
            value={selectedStatus ?? ""}
            onChange={(value) => {
              setSelectedStatus(
                value ? (value as RegistrationRequestStatus) : null,
              );
              resetPage();
            }}
            placeholder={t(Resources.RegistrationRequests.Status)}
          />
        </Box>
        <Box sx={{ minWidth: 200 }}>
          <BlDropdown
            name="type-filter"
            options={typeOptions}
            value={selectedType ?? ""}
            onChange={(value) => {
              setSelectedType(
                value ? (value as RegistrationRequestType) : null,
              );
              resetPage();
            }}
            placeholder={t(Resources.RegistrationRequests.Type)}
          />
        </Box>
      </Stack>
      <BlTanstackTable
        table={table}
        isLoading={isLoading}
        totalCount={totalCount}
      />
    </Box>
  );
};
